import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import CreatePost from '../components/CreatePost';
import EnquiryForm from '../components/EnquiryForm';
import { posts as mockPosts } from '../mockData';
import { personaProfiles } from '../personaProfiles';
import { getCurrentUser } from '../utils/auth';
import PersonaStats from '../components/PersonaStats';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { MapPin, Calendar, Trophy, Users, MessageCircle, Settings, Share2, Lock, UserPlus, Clock, X, Grid3X3, BarChart3, User, Mail, Image as ImageIcon, Plus } from 'lucide-react';
import PostCard from '../components/PostCard';
import { toast } from 'sonner';
import { postAPI, userAPI, getMediaUrl } from '../services/api';

const Profile = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [profileUser, setProfileUser] = useState(null);
  const [followStatus, setFollowStatus] = useState('none'); // none, following, requested, self
  const [userPosts, setUserPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [imageModal, setImageModal] = useState({ open: false, imageUrl: '', imageType: '' });
  const [activeTab, setActiveTab] = useState('posts');
  const [mediaViewerIndex, setMediaViewerIndex] = useState(null);
  const mediaRefs = useRef({});
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestMessage, setRequestMessage] = useState('');

  // Load current user
  useEffect(() => {
    const user = getCurrentUser();
    setCurrentUser(user);
  }, []);

  // Fetch profile user data
  useEffect(() => {
    const fetchProfileUser = async () => {
      if (!username) return;
      
      try {
        setLoading(true);
        const response = await userAPI.getUserProfile(username);
        setProfileUser(response.data);
        
        // Check follow status with this profile
        if (currentUser && response.data._id) {
          try {
            const statusRes = await userAPI.getFollowStatus(response.data._id);
            setFollowStatus(statusRes.data.status || 'none');
          } catch (error) {
            console.error('Error checking follow status:', error);
          }
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
        toast.error('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    fetchProfileUser();
  }, [username, currentUser]);

  const isOwnProfile = currentUser && profileUser && currentUser.username === profileUser.username;

  // Fetch user posts
  useEffect(() => {
    const fetchUserPosts = async () => {
      if (!profileUser) return;
      
      try {
        setLoading(true);
        const response = await postAPI.getUserPosts(username);
        console.log('Fetched user posts:', response.data);
        setUserPosts(response.data.posts || []);
      } catch (error) {
        console.error('Error fetching user posts:', error);
        console.error('Error status:', error.response?.status);
        console.error('Error detail:', error.response?.data);
        
        // Don't use mock data - show empty state or error
        setUserPosts([]);
        
        if (error.response?.status === 404) {
          // User not found - this is expected for demo profiles
          console.log('User not found, showing empty posts');
        } else if (error.response?.status === 401) {
          toast.error('Please login to view posts');
        } else {
          toast.error('Failed to load posts');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserPosts();
  }, [username, profileUser]);

  // Auto-scroll to selected media post after viewer renders
  useEffect(() => {
    if (mediaViewerIndex !== null && mediaRefs.current[mediaViewerIndex]) {
      const timer = setTimeout(() => {
        mediaRefs.current[mediaViewerIndex]?.scrollIntoView({ behavior: 'instant', block: 'start' });
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [mediaViewerIndex]);


  const handleFollowToggle = async () => {
    if (!profileUser) return;
    
    try {
      if (followStatus === 'following') {
        // Unfollow
        await userAPI.unfollowUser(profileUser._id);
        setFollowStatus('none');
        setProfileUser(prev => ({
          ...prev,
          followers: Math.max((prev.followers || 0) - 1, 0)
        }));
        toast.success('Unfollowed');
      } else if (followStatus === 'requested') {
        // Cancel request
        await userAPI.cancelFollowRequest(profileUser._id);
        setFollowStatus('none');
        toast.success('Request cancelled');
      } else {
        // New follow attempt
        if (profileUser.isPrivate) {
          setShowRequestModal(true);
        } else {
          const res = await userAPI.followUser(profileUser._id);
          if (res.data.status === 'requested') {
            setFollowStatus('requested');
            toast.success('Follow request sent');
          } else {
            setFollowStatus('following');
            setProfileUser(prev => ({
              ...prev,
              followers: (prev.followers || 0) + 1
            }));
            toast.success('Following');
          }
        }
      }
    } catch (error) {
      console.error('Error toggling follow:', error);
      toast.error(error.response?.data?.detail || 'Failed to update follow status');
    }
  };

  const handleSendFollowRequest = async () => {
    if (!profileUser) return;
    try {
      await userAPI.followUser(profileUser._id, requestMessage);
      setFollowStatus('requested');
      setShowRequestModal(false);
      setRequestMessage('');
      toast.success('Follow request sent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send request');
    }
  };

  const handlePostCreated = () => {
    // Refresh posts by re-fetching
    console.log('Post created, refreshing profile posts for username:', username);
    const fetchUserPosts = async () => {
      try {
        const response = await postAPI.getUserPosts(username);
        console.log('Refreshed posts:', response.data.posts);
        setUserPosts(response.data.posts || []);
        toast.success('Post added to your profile!');
      } catch (error) {
        console.error('Error refreshing posts:', error);
        toast.error('Post created but failed to refresh. Please reload the page.');
      }
    };
    fetchUserPosts();
  };

  const handleLike = async (postId) => {
    const post = userPosts.find((p) => (p._id || p.id) === postId);
    if (!post) {
      toast.error('Post not found');
      return;
    }
    
    const isLiked = post.liked || false;
    const currentUserId = localStorage.getItem('userId');

    // Optimistically update UI
    setUserPosts((prevPosts) =>
      prevPosts.map((p) => {
        if ((p._id || p.id) === postId) {
          // Ensure likes is an array
          const currentLikes = Array.isArray(p.likes) ? p.likes : [];
          
          return {
            ...p,
            liked: !isLiked,
            likes: isLiked 
              ? currentLikes.filter((id) => id !== currentUserId)
              : [...currentLikes, currentUserId],
          };
        }
        return p;
      })
    );

    try {
      const response = isLiked 
        ? await postAPI.unlikePost(postId)
        : await postAPI.likePost(postId);
      
      console.log('Like API response:', response.data);
      
      // Check if API call was successful
      if (response.data && response.data.success === false) {
        throw new Error(response.data.message || 'Like operation failed');
      }
      
      // Success - no error message needed, UI already updated
      return; // Exit successfully
    } catch (error) {
      console.error('Error liking post:', error);
      console.error('Error details:', {
        status: error.response?.status,
        data: error.response?.data,
        postId: postId
      });
      
      // Revert optimistic update on error using functional update
      setUserPosts((prevPosts) =>
        prevPosts.map((p) => {
          if ((p._id || p.id) === postId) {
            // Ensure likes is an array
            const currentLikes = Array.isArray(p.likes) ? p.likes : [];
            
            return {
              ...p,
              liked: isLiked, // Revert to original
              likes: isLiked 
                ? [...currentLikes, currentUserId]
                : currentLikes.filter((id) => id !== currentUserId),
            };
          }
          return p;
        })
      );
      
      const errorMsg = error.response?.data?.detail || 'Failed to like post. Please try again.';
      toast.error(errorMsg);
    }
  };

  const handleComment = async (postId, commentText) => {
    if (!commentText || !commentText.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    try {
      console.log('Adding comment to post:', postId, 'Text:', commentText);
      const response = await postAPI.addComment(postId, commentText);
      console.log('Comment API response:', response.data);
      
      const newComment = response.data.comment;

      // Update local state using functional update
      setUserPosts((prevPosts) =>
        prevPosts.map((p) => {
          if ((p._id || p.id) === postId) {
            // Ensure comments is an array
            const currentComments = Array.isArray(p.comments) ? p.comments : [];
            
            return {
              ...p,
              comments: [...currentComments, newComment],
            };
          }
          return p;
        })
      );

      toast.success('Comment added');
      return true; // Indicate success
    } catch (error) {
      console.error('Error adding comment:', error);
      console.error('Error details:', {
        status: error.response?.status,
        data: error.response?.data,
        postId: postId
      });
      
      const errorMsg = error.response?.data?.detail || 'Failed to add comment';
      toast.error(errorMsg);
      return false; // Indicate failure
    }
  };

  const handleDelete = async (postId) => {
    console.log('Profile: Attempting to delete post:', postId);
    try {
      const response = await postAPI.deletePost(postId);
      console.log('Profile: Delete response:', response.data);
      
      // Remove post from local state
      setUserPosts((prevPosts) => prevPosts.filter((p) => (p._id || p.id) !== postId));
      toast.success('Post deleted successfully');
    } catch (error) {
      console.error('Profile: Error deleting post:', error);
      console.error('Profile: Error details:', {
        status: error.response?.status,
        data: error.response?.data
      });
      
      const errorMsg = error.response?.data?.detail || 'Failed to delete post';
      toast.error(errorMsg);
    }
  };

  const handleShareProfile = async () => {
    const profileUrl = `${window.location.origin}/profile/${username}`;
    
    // Try Web Share API first (mobile devices)
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${profileUser?.name}'s Profile - SportiZn`,
          text: `Check out ${profileUser?.name}'s profile on SportiZn!`,
          url: profileUrl
        });
        toast.success('Profile shared!');
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error sharing profile:', error);
          // Fallback to clipboard
          copyToClipboard(profileUrl);
        }
      }
    } else {
      // Fallback to clipboard copy
      copyToClipboard(profileUrl);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        toast.success('Profile link copied to clipboard!');
      })
      .catch((err) => {
        console.error('Failed to copy:', err);
        toast.error('Failed to copy link');
      });
  };

  const openImageModal = (imageUrl, imageType) => {
    // Always open modal, even if no actual image uploaded (will show gradient fallback)
    setImageModal({ open: true, imageUrl: imageUrl || '', imageType });
  };

  const closeImageModal = () => {
    setImageModal({ open: false, imageUrl: '', imageType: '' });
  };

  // Close modal on ESC key
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && imageModal.open) {
        closeImageModal();
      }
    };

    if (imageModal.open) {
      document.addEventListener('keydown', handleEscKey);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [imageModal.open]);

  return (
    <>
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 pt-20">
        {/* Skeleton Loading State */}
        {loading && !profileUser ? (
          <Card className="overflow-hidden shadow-lg mt-4" data-testid="profile-skeleton">
            <div className="relative">
              <Skeleton className="h-64 w-full rounded-none" />
              <div className="absolute -bottom-20 left-8">
                <Skeleton className="w-40 h-40 rounded-full border-4 border-background" />
              </div>
            </div>
            <CardContent className="pt-24 pb-6">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between">
                <div className="flex-1 space-y-3">
                  <Skeleton className="h-8 w-48" />
                  <Skeleton className="h-5 w-32" />
                  <Skeleton className="h-6 w-40 rounded-full" />
                  <Skeleton className="h-4 w-full max-w-md mt-4" />
                  <Skeleton className="h-4 w-3/4 max-w-sm" />
                  <div className="flex gap-4 mt-4">
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-4 w-36" />
                  </div>
                </div>
                <div className="flex gap-3 mt-4 md:mt-0">
                  <Skeleton className="h-10 w-28 rounded-md" />
                  <Skeleton className="h-10 w-20 rounded-md" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-6 mt-8 pt-6 border-t border-border">
                {[0, 1, 2].map(i => (
                  <div key={i} className="flex flex-col items-center gap-2">
                    <Skeleton className="h-7 w-10" />
                    <Skeleton className="h-3 w-14" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ) : (
        <>
        {/* Cover Image */}
        <Card className="overflow-hidden shadow-lg mt-4">
          <div className="relative">
            <div
              className={`h-64 bg-cover bg-center cursor-pointer hover:opacity-90 transition-opacity ${!profileUser?.coverImage ? 'bg-brand-gradient' : ''}`}
              style={{
                backgroundImage: profileUser?.coverImage 
                  ? `url(${profileUser.coverImage})`
                  : undefined,
              }}
              onClick={() => openImageModal(profileUser?.coverImage, 'cover')}
              title="Click to view cover image"
            ></div>
            <div className="absolute -bottom-20 left-8">
              <Avatar 
                className="w-40 h-40 border-4 border-white shadow-lg cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => openImageModal(profileUser?.profileImage, 'profile')}
                title="Click to view profile picture"
              >
                <AvatarImage src={profileUser?.profileImage} alt={profileUser?.name || 'User'} />
                <AvatarFallback className="text-4xl">{profileUser?.name?.[0] || 'U'}</AvatarFallback>
              </Avatar>
            </div>
          </div>

          <CardContent className="pt-24 pb-6">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between">
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-foreground">{profileUser?.name || 'User'}</h1>
                <div className="flex items-center gap-2">
                  <p className="text-muted-foreground text-lg">@{profileUser?.username || 'username'}</p>
                  {profileUser?.isPrivate && (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded-full" data-testid="private-badge">
                      <Lock className="w-3 h-3" /> Private
                    </span>
                  )}
                </div>
                <Badge className="mt-2 badge-primary text-sm">
                  {profileUser?.persona || 'Athlete'} • {profileUser?.sports?.join(', ') || profileUser?.sport || 'Sport'}
                </Badge>
                {/* Hide bio/details for private profiles when not following */}
                {(isOwnProfile || !profileUser?.isPrivate || followStatus === 'following') && (
                  <>
                    <p className="mt-4 text-foreground max-w-2xl">{profileUser?.bio || 'No bio yet'}</p>
                    {profileUser?.organization && (
                      <p className="mt-2 text-muted-foreground flex items-center">
                        <Users className="w-4 h-4 mr-2" />
                        {profileUser.organization}
                      </p>
                    )}
                  </>
                )}
                <div className="flex flex-wrap gap-4 mt-4 text-sm text-muted-foreground">
                  <div className="flex items-center">
                    <MapPin className="w-4 h-4 mr-1" />
                    {profileUser?.location || 'Location not set'}
                  </div>
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-1" />
                    Joined January 2024
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-4 md:mt-0">
                {isOwnProfile ? (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => navigate('/edit-profile')}
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Edit Profile
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={handleShareProfile}
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      Share
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      className={followStatus === 'following' ? '' : followStatus === 'requested' ? 'bg-muted text-muted-foreground hover:bg-destructive hover:text-white' : 'btn-primary'}
                      variant={followStatus === 'following' ? 'outline' : 'default'}
                      onClick={handleFollowToggle}
                      data-testid="follow-btn"
                    >
                      {followStatus === 'following' ? (
                        <><Users className="w-4 h-4 mr-2" /> Following</>
                      ) : followStatus === 'requested' ? (
                        <><Clock className="w-4 h-4 mr-2" /> Requested</>
                      ) : (
                        <><UserPlus className="w-4 h-4 mr-2" /> {profileUser?.isPrivate ? 'Request to Follow' : 'Follow'}</>
                      )}
                    </Button>
                    {followStatus === 'following' && (
                      <Button variant="outline" onClick={() => navigate(`/messages?userId=${profileUser._id}`)}>
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Message
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 mt-8 pt-6 border-t border-border">
              <div 
                className={`text-center rounded-lg p-2 transition-colors ${isOwnProfile || !profileUser?.isPrivate || followStatus === 'following' ? 'cursor-pointer hover:bg-muted/50' : ''}`}
                onClick={() => { if (isOwnProfile || !profileUser?.isPrivate || followStatus === 'following') setActiveTab('posts'); }}
              >
                <p className="text-2xl font-bold text-primary">
                  {(profileUser?.postsCount || 0).toLocaleString()}
                </p>
                <p className="text-sm text-muted-foreground">Posts</p>
              </div>
              <div 
                className={`text-center rounded-lg p-2 transition-colors ${isOwnProfile || !profileUser?.isPrivate || followStatus === 'following' ? 'cursor-pointer hover:bg-muted/50' : ''}`}
                onClick={() => { if (isOwnProfile || !profileUser?.isPrivate || followStatus === 'following') navigate(`/connections?tab=followers&userId=${profileUser?._id}`); }}
              >
                <p className="text-2xl font-bold text-primary">
                  {(profileUser?.followers || 0).toLocaleString()}
                </p>
                <p className="text-sm text-muted-foreground">Followers</p>
              </div>
              <div 
                className={`text-center rounded-lg p-2 transition-colors ${isOwnProfile || !profileUser?.isPrivate || followStatus === 'following' ? 'cursor-pointer hover:bg-muted/50' : ''}`}
                onClick={() => { if (isOwnProfile || !profileUser?.isPrivate || followStatus === 'following') navigate(`/connections?tab=following&userId=${profileUser?._id}`); }}
              >
                <p className="text-2xl font-bold text-primary">
                  {(profileUser?.following || 0).toLocaleString()}
                </p>
                <p className="text-sm text-muted-foreground">Following</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs - Hidden for private profiles when not following */}
        <div className="mt-6">
          {!isOwnProfile && profileUser?.isPrivate && followStatus !== 'following' ? (
            <Card className="border border-border shadow-sm">
              <CardContent className="py-16 text-center">
                <div className="w-20 h-20 rounded-full border-2 border-muted-foreground/30 flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-10 h-10 text-muted-foreground" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-2">This Account is Private</h3>
                <p className="text-muted-foreground max-w-sm mx-auto mb-6">
                  Follow this account to see their posts, stats, and details.
                </p>
                {followStatus === 'requested' ? (
                  <Button variant="outline" className="bg-muted text-muted-foreground" disabled data-testid="private-requested-btn">
                    <Clock className="w-4 h-4 mr-2" /> Follow Request Sent
                  </Button>
                ) : (
                  <Button className="btn-primary" onClick={handleFollowToggle} data-testid="private-follow-btn">
                    <UserPlus className="w-4 h-4 mr-2" /> Request to Follow
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
          <>
          {/* Instagram-style Tab Bar */}
          {(() => {
            const isProfessional = profileUser && ['physio', 'nutritionist', 'coach', 'club'].includes(profileUser.persona);
            const tabs = [
              { id: 'posts', label: 'Posts', icon: Grid3X3 },
              { id: 'media', label: 'Media', icon: ImageIcon },
              { id: 'stats', label: 'Stats', icon: BarChart3 },
              ...(isProfessional ? [{ id: 'enquiry', label: 'Enquiry', icon: Mail }] : [])
            ];
            const activeIndex = tabs.findIndex(t => t.id === activeTab);
            const tabWidth = 100 / tabs.length;

            return (
              <div className="border-t border-border relative" data-testid="profile-tabs">
                <div className="flex">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      data-testid={`profile-tab-${tab.id}`}
                      className={`flex-1 flex flex-col items-center justify-center gap-1 py-3 transition-colors duration-200 relative ${
                        activeTab === tab.id
                          ? 'text-primary'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <tab.icon className="w-5 h-5" strokeWidth={activeTab === tab.id ? 2.5 : 1.5} />
                      <span className="text-[11px] font-medium tracking-wide hidden sm:block">{tab.label}</span>
                    </button>
                  ))}
                </div>
                {/* Animated underline indicator */}
                <div
                  className="absolute bottom-0 h-[2px] bg-primary transition-all duration-300 ease-in-out"
                  style={{
                    width: `${tabWidth}%`,
                    left: `${activeIndex * tabWidth}%`,
                  }}
                />
              </div>
            );
          })()}

          {/* Tab Content */}
          <div className="mt-4">
            {activeTab === 'posts' && (
              <div className="space-y-6" data-testid="profile-tab-content-posts">
                {userPosts.length > 0 ? (
                  userPosts.map(post => (
                    <PostCard
                      key={post._id || post.id}
                      post={post}
                      onLike={handleLike}
                      onComment={handleComment}
                      onDelete={handleDelete}
                    />
                  ))
                ) : loading ? (
                  <div className="space-y-3" data-testid="profile-posts-skeleton">
                    {[0, 1].map((i) => (
                      <div key={i} className="rounded-2xl bg-card shadow-sm overflow-hidden">
                        <div className="flex items-center space-x-3 px-5 py-4">
                          <Skeleton className="w-10 h-10 rounded-full" />
                          <div className="flex-1 space-y-2">
                            <Skeleton className="h-3.5 w-28" />
                            <Skeleton className="h-3 w-40" />
                          </div>
                        </div>
                        <div className="px-5 pb-3 space-y-2">
                          <Skeleton className="h-3.5 w-full" />
                          <Skeleton className="h-3.5 w-2/3" />
                        </div>
                        <Skeleton className="w-full h-56" />
                        <div className="flex items-center space-x-6 px-5 py-3">
                          <Skeleton className="h-5 w-14" />
                          <Skeleton className="h-5 w-10" />
                          <Skeleton className="h-5 w-14" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <Trophy className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                      <p className="text-foreground font-semibold text-lg mb-2">No posts yet</p>
                      <p className="text-muted-foreground mb-4">
                        {isOwnProfile 
                          ? "Start sharing your sports journey! Click the + button to create your first post."
                          : "This user hasn't posted anything yet."}
                      </p>
                      {isOwnProfile && (
                        <Button 
                          onClick={() => setShowCreatePost(true)}
                          className="mt-2 btn-primary"
                        >
                          Create Post
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {activeTab === 'stats' && (
              <div data-testid="profile-tab-content-stats">
                <PersonaStats user={profileUser} />
              </div>
            )}

            {activeTab === 'media' && (
              <div data-testid="profile-tab-content-media">
                {(() => {
                  const mediaPosts = userPosts.filter(p => p.mediaUrl || p.imageUrl || (p.mediaUrls && p.mediaUrls.length > 0));
                  if (mediaPosts.length === 0) {
                    return (
                      <Card>
                        <CardContent className="p-12 text-center">
                          <ImageIcon className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                          <p className="text-foreground font-semibold text-lg mb-2">No media yet</p>
                          <p className="text-muted-foreground">
                            {isOwnProfile
                              ? "Share photos and videos to see them here."
                              : "This user hasn't shared any media yet."}
                          </p>
                        </CardContent>
                      </Card>
                    );
                  }

                  // Media viewer mode — all posts in order, auto-scroll to clicked one
                  if (mediaViewerIndex !== null) {
                    return (
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <button
                            onClick={() => setMediaViewerIndex(null)}
                            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                            data-testid="media-viewer-back"
                          >
                            <X className="w-4 h-4" /> Back to grid
                          </button>
                          <span className="text-xs text-muted-foreground">
                            {mediaViewerIndex + 1} / {mediaPosts.length}
                          </span>
                        </div>
                        <div className="space-y-4">
                          {mediaPosts.map((post, idx) => (
                            <div
                              key={post._id || post.id}
                              ref={(el) => { mediaRefs.current[idx] = el; }}
                            >
                              <PostCard
                                post={post}
                                onLike={handleLike}
                                onComment={handleComment}
                                onDelete={handleDelete}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }

                  // Grid mode
                  return (
                    <div className="grid grid-cols-3 gap-1 rounded-lg overflow-hidden">
                      {mediaPosts.map((post, index) => {
                        const src = post.mediaUrl || post.imageUrl || (post.mediaUrls && post.mediaUrls[0]);
                        const isVideo = post.mediaType === 'video' || (typeof src === 'string' && src.match(/\.(mp4|mov|webm)/i));
                        const resolvedSrc = getMediaUrl(src);
                        return (
                          <div
                            key={post._id || post.id}
                            className="aspect-square bg-muted cursor-pointer relative group overflow-hidden"
                            onClick={() => setMediaViewerIndex(index)}
                          >
                            {isVideo ? (
                              <video
                                src={resolvedSrc}
                                className="w-full h-full object-cover"
                                muted
                                loop
                                playsInline
                                autoPlay
                              />
                            ) : (
                              <img
                                src={resolvedSrc}
                                alt=""
                                className="w-full h-full object-cover"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            )}
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                              <div className="flex gap-4 text-white text-sm font-semibold">
                                {post.likes != null && <span>&#9829; {Array.isArray(post.likes) ? post.likes.length : (post.likes || 0)}</span>}
                                {post.comments != null && <span>&#128172; {Array.isArray(post.comments) ? post.comments.length : (post.comments || 0)}</span>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            )}

            {activeTab === 'enquiry' && profileUser && ['physio', 'nutritionist', 'coach', 'club'].includes(profileUser.persona) && (
              <div data-testid="profile-tab-content-enquiry">
                <EnquiryForm professionalId={profileUser._id} professionalName={profileUser.name} />
              </div>
            )}
          </div>
          </>
          )}
        </div>
        </>
        )}
      </div>

      {/* Floating Create Post Button — mobile only, own profile only */}
      {isOwnProfile && (
        <button
          onClick={() => setShowCreatePost(true)}
          className="fixed right-14 bottom-[76px] z-50 md:hidden w-11 h-11 rounded-full bg-primary text-white shadow-xl flex items-center justify-center active:scale-90 transition-transform hover:shadow-2xl"
          data-testid="floating-create-post-btn"
          aria-label="Create post"
        >
          <Plus className="w-5 h-5" strokeWidth={2.5} />
        </button>
      )}

      {showCreatePost && (
        <CreatePost 
          onClose={() => setShowCreatePost(false)}
          onPostCreated={handlePostCreated}
        />
      )}

      {/* Image Modal */}
      {imageModal.open && (
        <div 
          className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center p-4"
          onClick={closeImageModal}
        >
          <div className="relative max-w-7xl max-h-full">
            {/* Close button */}
            <button
              onClick={closeImageModal}
              className="absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70 transition-all z-10"
              title="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Edit button - only show on own profile */}
            {isOwnProfile && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  closeImageModal();
                  navigate('/edit-profile');
                }}
                className="absolute top-4 right-16 text-white bg-primary hover:bg-primary/80 rounded-full px-4 py-2 transition-all z-10 flex items-center gap-2"
                title="Edit Image"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
                Edit {imageModal.imageType === 'profile' ? 'Profile Picture' : 'Cover Image'}
              </button>
            )}

            {/* Image */}
            {imageModal.imageUrl ? (
              <img
                src={imageModal.imageUrl}
                alt={imageModal.imageType === 'profile' ? 'Profile Picture' : 'Cover Image'}
                className="max-w-full max-h-[90vh] object-contain rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <div className="bg-brand-gradient p-32 rounded-lg">
                <p className="text-white text-center text-xl">
                  {imageModal.imageType === 'profile' ? 'No Profile Picture' : 'No Cover Image'}
                </p>
                {isOwnProfile && (
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      closeImageModal();
                      navigate('/edit-profile');
                    }}
                    className="mt-4 bg-white text-primary hover:bg-gray-100"
                  >
                    Upload {imageModal.imageType === 'profile' ? 'Profile Picture' : 'Cover Image'}
                  </Button>
                )}
              </div>
            )}

            {/* Image type label */}
            <div className="absolute bottom-4 left-4 bg-black bg-opacity-50 text-white px-4 py-2 rounded-lg">
              {imageModal.imageType === 'profile' ? 'Profile Picture' : 'Cover Image'}
            </div>
          </div>
        </div>
      )}
    </div>

    {/* Follow Request Modal */}
    {showRequestModal && (
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowRequestModal(false)}>
        <Card className="w-full max-w-md" onClick={e => e.stopPropagation()}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Request to Follow</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowRequestModal(false)} data-testid="close-request-modal">
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex items-center gap-3 mb-4">
              <Avatar className="w-10 h-10">
                <AvatarImage src={profileUser?.profileImage} />
                <AvatarFallback>{profileUser?.name?.[0]}</AvatarFallback>
              </Avatar>
              <div>
                <p className="font-medium">{profileUser?.name}</p>
                <p className="text-sm text-muted-foreground flex items-center gap-1"><Lock className="w-3 h-3" /> Private account</p>
              </div>
            </div>
            <Input
              placeholder="Add a note (optional)..."
              value={requestMessage}
              onChange={e => setRequestMessage(e.target.value)}
              className="mb-4"
              data-testid="request-message-input"
              maxLength={200}
            />
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowRequestModal(false)}>Cancel</Button>
              <Button className="flex-1 btn-primary" onClick={handleSendFollowRequest} data-testid="send-request-btn">Send Request</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )}
    </>
  );
};

export default Profile;
